import {
    type RewriteRule,
    type ValidationResult,
    validateRewriteRuleConfig,
} from '@lib/string-rewriter';
import { isObject } from 'lodash';

export type DeeplinkRewriteRules = {
    ios: RewriteRule | null;
    android: RewriteRule | null;
};

export type AdditionalProductRenderOptions = {
    withDescription: {
        inPreview: boolean;
        inDetail: boolean;
        inStore: boolean;
    };
    deeplinkRewriteRules: DeeplinkRewriteRules | null;
};

const DEFAULT_ADDITIONAL_PRODUCT_RENDER_OPTIONS: AdditionalProductRenderOptions =
    {
        withDescription: {
            inPreview: false,
            inDetail: true,
            inStore: true,
        },
        deeplinkRewriteRules: null,
    };

const sanitizeDeeplinkRewriteRules = (
    template: unknown
): DeeplinkRewriteRules | null => {
    if (isObject(template)) {
        const iosValidationResult =
            'ios' in template
                ? validateRewriteRuleConfig(template.ios)
                : ({
                      ok: false,
                      errors: ['No iOS rewrite rule config provided.'],
                  } satisfies ValidationResult);
        const androidValidationResult =
            'android' in template
                ? validateRewriteRuleConfig(template.android)
                : ({
                      ok: false,
                      errors: ['No Android rewrite rule config provided.'],
                  } satisfies ValidationResult);
        const ios = iosValidationResult.ok ? iosValidationResult.result : null;
        const android = androidValidationResult.ok
            ? androidValidationResult.result
            : null;
        if (ios || android) {
            return { ios, android };
        }
    }
    return null;
};

export const sanitizeAdditionalProductConfig = (
    config: unknown
): AdditionalProductRenderOptions => {
    if (!isObject(config)) return DEFAULT_ADDITIONAL_PRODUCT_RENDER_OPTIONS;
    const withDescription =
        'withDescription' in config && isObject(config.withDescription)
            ? {
                  inPreview:
                      'inPreview' in config.withDescription &&
                      typeof config.withDescription.inPreview === 'boolean'
                          ? config.withDescription.inPreview
                          : DEFAULT_ADDITIONAL_PRODUCT_RENDER_OPTIONS
                                .withDescription.inPreview,
                  inDetail:
                      'inDetail' in config.withDescription &&
                      typeof config.withDescription.inDetail === 'boolean'
                          ? config.withDescription.inDetail
                          : DEFAULT_ADDITIONAL_PRODUCT_RENDER_OPTIONS
                                .withDescription.inDetail,
                  inStore:
                      'inStore' in config.withDescription &&
                      typeof config.withDescription.inStore === 'boolean'
                          ? config.withDescription.inStore
                          : DEFAULT_ADDITIONAL_PRODUCT_RENDER_OPTIONS
                                .withDescription.inStore,
              }
            : DEFAULT_ADDITIONAL_PRODUCT_RENDER_OPTIONS.withDescription;

    const deeplinkRewriteRules =
        'deeplinkRewriteRules' in config
            ? sanitizeDeeplinkRewriteRules(config.deeplinkRewriteRules)
            : null;

    return {
        withDescription,
        deeplinkRewriteRules,
    };
};
